/**
 * Vendor rules engine (Spec v0.3).
 *
 * Canonical rule shape:
 *   vendorMatch, glCodeDefault, propertyCodeDefault, taxGl,
 *   emitDistributionLines, lineItemRules[], note?, createdAt
 *
 * Legacy flat rules (vendorName, glCode, propertyCode) are migrated on read/write.
 */

import { getAllRules, putRule, deleteRule, clearRules } from './storage.js';
import { scheduleRulesBackup } from './rules-backup.js';

export const STRATEGY_EXACT      = 'exact';
export const STRATEGY_NORMALIZED = 'normalized';
export const STRATEGY_FUZZY      = 'fuzzy';

export const DEFAULT_STRATEGY  = STRATEGY_NORMALIZED;
export const DEFAULT_THRESHOLD = 0.85;

export const EXPORT_VERSION = 2;
export const EXPORT_MARKER  = 'wiseentry-rules';
export const LEGACY_MARKER  = 'wiseitoc-rules';

const SUFFIX_RE = /\b(?:LLC|L\.L\.C\.|Inc|Inc\.|Incorporated|Corp|Corp\.|Corporation|Co|Co\.|Company|Ltd|Ltd\.|Limited|LP|LLP|PLLC|PC|PLC|GmbH|S\.A\.|SA)\b\.?/gi;

function normalize(s) {
  if (!s) return '';
  return String(s)
    .replace(SUFFIX_RE, '')
    .replace(/[,.()'"!?&]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/** Display / match string for a rule record (canonical or legacy). */
export function vendorKey(rule) {
  if (!rule) return '';
  return String(rule.vendorMatch || rule.vendorName || '').trim();
}

/**
 * Migrate legacy DB / import row to canonical v0.3 fields (mutates copy).
 */
export function toCanonicalRule(r) {
  if (!r || typeof r !== 'object') return r;
  const id = r.id;
  const out = { ...r };
  const vm = String(out.vendorMatch || out.vendorName || '').trim();
  out.vendorMatch = vm;
  out.vendorName = vm;
  out.glCodeDefault = String(out.glCodeDefault ?? out.glCode ?? '').trim();
  out.propertyCodeDefault = String(out.propertyCodeDefault ?? out.propertyCode ?? '').trim();
  out.taxGl = String(out.taxGl ?? out.glCodeDefault ?? out.glCode ?? '').trim();
  out.emitDistributionLines = !!out.emitDistributionLines;
  out.lineItemRules = Array.isArray(out.lineItemRules) ? out.lineItemRules.map(normalizeLineItemRule) : [];
  out.note = out.note != null ? String(out.note) : '';
  out.createdAt = out.createdAt || new Date().toISOString();
  if (id) out.id = id;
  delete out.glCode;
  delete out.propertyCode;
  return out;
}

function normalizeLineItemRule(lr) {
  return {
    matchOn:    lr.matchOn === 'keyword' ? 'keyword' : 'itemCode',
    matchValue: String(lr.matchValue || ''),
    glCode:     String(lr.glCode || ''),
    propertyCode: lr.propertyCode != null && lr.propertyCode !== ''
      ? String(lr.propertyCode)
      : null
  };
}

/**
 * Resolve GL / property for one line item (detail mode).
 * @returns {{ glCode: string, propertyCode: string, badge: string }}
 */
export function resolveLineItemCoding(rule, lineItem) {
  const r = toCanonicalRule(rule);
  const desc = String(lineItem?.description || '');
  const ic = lineItem?.item_code != null ? String(lineItem.item_code) : null;
  for (const lr of r.lineItemRules) {
    if (lr.matchOn === 'itemCode' && ic != null && lr.matchValue === ic) {
      return {
        glCode: lr.glCode,
        propertyCode: lr.propertyCode ?? r.propertyCodeDefault,
        badge: `rule: ${lr.matchValue}`
      };
    }
    if (lr.matchOn === 'keyword' && lr.matchValue && desc.toLowerCase().includes(String(lr.matchValue).toLowerCase())) {
      return {
        glCode: lr.glCode,
        propertyCode: lr.propertyCode ?? r.propertyCodeDefault,
        badge: `rule: ${lr.matchValue}`
      };
    }
  }
  return {
    glCode: r.glCodeDefault,
    propertyCode: r.propertyCodeDefault,
    badge: 'default'
  };
}

/**
 * Build distribution preview lines for a file after a matched rule (v0.3 §06–§08).
 * @returns {{ mode: 'summary'|'detail', lines: Array }} | null
 */
export function buildDistribution(file, rule) {
  if (!file?.extracted || !rule) return null;
  const r = toCanonicalRule(rule);
  const e = { ...file.extracted, ...(file.edits || {}) };

  if (!r.emitDistributionLines) {
    const desc = summaryDescription(e);
    const amt = typeof e.total_amount === 'number' ? e.total_amount : 0;
    return {
      mode: 'summary',
      lines: [{
        kind: 'summary',
        description: desc,
        amount: amt,
        glCode: r.glCodeDefault,
        propertyCode: r.propertyCodeDefault,
        source: 'default',
        taxAmount: e.tax_amount
      }]
    };
  }

  const lines = [];
  const items = Array.isArray(e.line_items) ? e.line_items : [];
  for (let i = 0; i < items.length; i++) {
    const li = items[i];
    const lt = typeof li.line_total === 'number' ? li.line_total : 0;
    if (!lt) continue;
    const { glCode, propertyCode, badge } = resolveLineItemCoding(r, li);
    lines.push({
      kind: 'item',
      lineIndex: i,
      description: li.description != null ? String(li.description) : '',
      amount: lt,
      glCode,
      propertyCode,
      source: badge
    });
  }
  const taxes = Array.isArray(e.sales_tax_lines) ? e.sales_tax_lines : [];
  for (let j = 0; j < taxes.length; j++) {
    const t = taxes[j];
    const amt = typeof t.amount === 'number' ? t.amount : 0;
    if (!amt) continue;
    lines.push({
      kind: 'tax',
      taxIndex: j,
      description: t.label != null ? String(t.label) : 'Tax',
      amount: amt,
      glCode: r.taxGl,
      propertyCode: r.propertyCodeDefault,
      source: 'tax'
    });
  }
  return { mode: 'detail', lines };
}

function summaryDescription(e) {
  const v = e.vendor_name || '';
  const inv = e.invoice_number;
  if (inv) return `${v} — Invoice ${inv}`.trim();
  if (Array.isArray(e.line_items) && e.line_items[0]?.description) {
    return String(e.line_items[0].description).slice(0, 120);
  }
  return v || 'Invoice';
}

/** Sum of distribution line amounts (detail) for reconciliation (tolerance $0.02). */
export function distributionTotalDelta(file) {
  const d = file.distribution;
  if (!d || d.mode !== 'detail' || !Array.isArray(d.lines)) return null;
  const e = { ...file.extracted, ...(file.edits || {}) };
  const total = typeof e.total_amount === 'number' ? e.total_amount : null;
  if (total == null) return null;
  const sum = d.lines.reduce((s, L) => s + (typeof L.amount === 'number' ? L.amount : 0), 0);
  const delta = Math.round((total - sum) * 100) / 100;
  if (Math.abs(delta) <= 0.02) return null;
  return delta;
}

export function fileIsUncoded(file) {
  if (file.status !== 'done') return false;
  const d = file.distribution;
  if (d && Array.isArray(d.lines) && d.lines.length) {
    return d.lines.some(L => !String(L.glCode || '').trim() && !String(L.propertyCode || '').trim());
  }
  const c = file.coding;
  return !c || (!c.glCode && !c.propertyCode);
}

/* -------- Rule CRUD -------- */

export async function listRules() {
  const rules = await getAllRules();
  const canon = rules.map(toCanonicalRule);
  canon.sort((a, b) => vendorKey(a).localeCompare(vendorKey(b), undefined, { sensitivity: 'base' }));
  return canon;
}

export async function saveRule(rule) {
  const r = toCanonicalRule({
    id:          rule.id || cryptoRandomId(),
    vendorMatch: String(rule.vendorMatch || rule.vendorName || '').trim(),
    glCodeDefault: String(rule.glCodeDefault ?? rule.glCode ?? '').trim(),
    propertyCodeDefault: String(rule.propertyCodeDefault ?? rule.propertyCode ?? '').trim(),
    taxGl: String(rule.taxGl ?? rule.glCodeDefault ?? rule.glCode ?? '').trim(),
    emitDistributionLines: !!rule.emitDistributionLines,
    lineItemRules: rule.lineItemRules || [],
    note:        rule.note != null ? String(rule.note) : '',
    createdAt:   rule.createdAt || new Date().toISOString()
  });
  if (!r.vendorMatch) throw new Error('Vendor name is required.');
  if (!r.glCodeDefault && !r.propertyCodeDefault) {
    throw new Error('At least one of GL code / property code must be set.');
  }
  await putRule(r);
  scheduleRulesBackup();
  return r;
}

export async function removeRule(id) {
  await deleteRule(id);
  scheduleRulesBackup();
}

export async function removeAllRules() {
  await clearRules();
  scheduleRulesBackup();
}

/**
 * One-shot: rewrite all rules in IndexedDB to canonical v0.3 shape.
 */
export async function migrateAllRulesToCanonical() {
  const raw = await getAllRules();
  for (const r of raw) {
    await putRule(toCanonicalRule(r));
  }
  return raw.length;
}

/* -------- Matching -------- */

export async function matchVendor(vendorName, strategy = DEFAULT_STRATEGY, threshold = DEFAULT_THRESHOLD) {
  if (!vendorName) return null;
  const rules = await listRules();
  if (rules.length === 0) return null;

  if (strategy === STRATEGY_EXACT) {
    for (const r of rules) {
      if (vendorKey(r) === vendorName) return { rule: r, score: 1 };
    }
    return null;
  }

  if (strategy === STRATEGY_NORMALIZED) {
    const target = normalize(vendorName);
    for (const r of rules) {
      if (normalize(vendorKey(r)) === target) return { rule: r, score: 1 };
    }
    return null;
  }

  if (strategy === STRATEGY_FUZZY) {
    const target = normalize(vendorName);
    let best = null;
    for (const r of rules) {
      const score = similarity(target, normalize(vendorKey(r)));
      if (score >= threshold && (!best || score > best.score)) {
        best = { rule: r, score };
      }
    }
    return best;
  }

  return null;
}

/**
 * Apply rules to files in-place; sets coding + distribution per v0.3.
 */
export async function applyRulesToFiles(files, strategy = DEFAULT_STRATEGY, threshold = DEFAULT_THRESHOLD) {
  let matched = 0, unmatched = 0;
  for (const f of files) {
    if (f.status !== 'done' || !f.extracted) continue;
    if (f.coding && f.coding.source === 'manual') continue;

    const m = await matchVendor(f.extracted.vendor_name, strategy, threshold);
    if (m) {
      const rule = toCanonicalRule(m.rule);
      f.coding = {
        glCode:       rule.glCodeDefault,
        propertyCode: rule.propertyCodeDefault,
        source:       'rule',
        ruleId:       rule.id,
        score:        m.score,
        detailMode:   !!rule.emitDistributionLines
      };
      f.distribution = buildDistribution(f, rule);
      f.distributionEdits = null;
      matched++;
    } else {
      if (!f.coding) f.coding = { glCode: '', propertyCode: '', source: null };
      f.distribution = null;
      unmatched++;
    }
  }
  return { matched, unmatched };
}

/* -------- Import / Export -------- */

export async function exportRulesJson() {
  const rules = await listRules();
  return JSON.stringify({
    marker:     EXPORT_MARKER,
    version:    EXPORT_VERSION,
    exportedAt: new Date().toISOString(),
    rules: rules.map(r => ({
      vendorMatch:            r.vendorMatch,
      glCodeDefault:          r.glCodeDefault,
      propertyCodeDefault:    r.propertyCodeDefault,
      taxGl:                  r.taxGl,
      emitDistributionLines:  r.emitDistributionLines,
      lineItemRules:          r.lineItemRules,
      note:                   r.note,
      createdAt:              r.createdAt
    }))
  }, null, 2);
}

export async function importRulesJson(jsonText, mode = 'replace') {
  let parsed;
  try { parsed = JSON.parse(jsonText); }
  catch { throw new Error('Malformed rules file.'); }

  const marker = parsed?.marker;
  if (marker !== EXPORT_MARKER && marker !== LEGACY_MARKER) {
    throw new Error('Not a WiseEntry rules export.');
  }
  if (!Array.isArray(parsed.rules)) {
    throw new Error('Malformed rules file: missing "rules" array.');
  }

  if (mode === 'replace') {
    await removeAllRules();
  }

  let added = 0;
  const ver = parsed.version || 1;
  for (const r of parsed.rules) {
    const row = ver >= 2
      ? {
        vendorMatch: r.vendorMatch,
        glCodeDefault: r.glCodeDefault,
        propertyCodeDefault: r.propertyCodeDefault,
        taxGl: r.taxGl,
        emitDistributionLines: r.emitDistributionLines,
        lineItemRules: r.lineItemRules,
        note: r.note,
        createdAt: r.createdAt
      }
      : {
        vendorMatch: r.vendorName,
        glCodeDefault: r.glCode,
        propertyCodeDefault: r.propertyCode,
        taxGl: r.glCode,
        emitDistributionLines: false,
        lineItemRules: [],
        note: r.note,
        createdAt: r.createdAt
      };
    if (!row.vendorMatch) continue;
    await saveRule(row);
    added++;
  }
  return { added };
}

/* -------- Util -------- */

function cryptoRandomId() {
  if (crypto.randomUUID) return crypto.randomUUID();
  const a = new Uint8Array(16);
  crypto.getRandomValues(a);
  return [...a].map(b => b.toString(16).padStart(2, '0')).join('');
}

function similarity(a, b) {
  if (a === b) return 1;
  if (!a || !b) return 0;
  const len = Math.max(a.length, b.length);
  return 1 - levenshtein(a, b) / len;
}

function levenshtein(a, b) {
  if (a === b) return 0;
  const m = a.length, n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  let prev = new Array(n + 1);
  let curr = new Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;
  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }
  return prev[n];
}
